import { useEffect, useRef, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { Textarea } from 'app/components/ui/textarea'
import { DropZone } from 'app/components/ui/drop-zone'
import { BaseAudioPlayer } from 'app/components/ui/audio-player'
import Spinner from 'app/components/Spinner'
import { MAX_GREETING_LENGTH } from './ivr-form'
import { useAudioPlaybackUrl } from './useAudioPlaybackUrl'

const ACCEPTED_AUDIO_TYPES = [`audio/mpeg`, `audio/wav`, `audio/x-m4a`, `audio/mp4`]
const MAX_FILE_SIZE_MB = 5

/** Extract filename from an S3 key like "ivr/loc-1/1234-greeting.mp3" */
export const filenameFromKey = (key: string) => {
  const parts = key.split(`/`)
  const full = parts[parts.length - 1] || key
  // Strip the timestamp prefix (e.g. "1234-greeting.mp3" → "greeting.mp3")
  const dashIdx = full.indexOf(`-`)
  return dashIdx > 0 ? full.slice(dashIdx + 1) : full
}

/** Inline player that fetches a presigned URL for an already-saved S3 audio key */
const SavedAudioPlayback = ({
  audioKey,
  id,
  activePlayerId,
  onPlay,
}: {
  audioKey: string
  id: string
  activePlayerId?: string
  onPlay?: (id: string) => void
}) => {
  const { url, loading } = useAudioPlaybackUrl(audioKey)
  if (loading) return <Spinner size={3} />
  if (!url) return null
  return <BaseAudioPlayer id={id} activePlayerId={activePlayerId} url={url} onPlay={onPlay} />
}

/** TTS textarea with character counter, or S3 file upload with drag-and-drop */
export const AudioInput = ({
  audioType,
  text,
  audioKey,
  id,
  activePlayerId,
  disabled,
  placeholder = `Thank you for calling [Office Name]. Describe your menu options here...`,
  onTextChange,
  onKeyChange,
  onFileSelect,
  onPlay,
}: {
  audioType: `tts` | `s3`
  text: string
  audioKey: string
  id?: string
  activePlayerId?: string
  disabled?: boolean
  placeholder?: string
  onTextChange: (val: string) => void
  onKeyChange: (val: string) => void
  onFileSelect?: (file: File | undefined) => void
  onPlay?: (id: string) => void
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingFile, setPendingFile] = useState<File | undefined>()
  const [localUrl, setLocalUrl] = useState<string | undefined>()

  useEffect(() => {
    if (pendingFile) {
      const url = URL.createObjectURL(pendingFile)
      setLocalUrl(url)
      return () => URL.revokeObjectURL(url)
    }
    setLocalUrl(undefined)
  }, [pendingFile])

  const handleFile = (file: File) => {
    setPendingFile(file)
    onFileSelect?.(file)
  }

  const handleRemove = () => {
    setPendingFile(undefined)
    onFileSelect?.(undefined)
    onKeyChange(``)
  }

  if (audioType === `s3`) {
    // File selected (pending upload) or already saved — show filename + player + remove
    if (pendingFile || audioKey) {
      const displayName = pendingFile ? pendingFile.name : filenameFromKey(audioKey)
      return (
        <div className="mt-2 flex flex-col gap-2 rounded border border-default bg-surface-subtle p-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-medium">{displayName}</p>
            </div>
            <Button variant="tertiary" size="sm" aria-label="Remove" onClick={handleRemove} disabled={disabled}>
              Remove
            </Button>
          </div>
          {localUrl ? (
            <BaseAudioPlayer id={id} activePlayerId={activePlayerId} url={localUrl} onPlay={onPlay} />
          ) : audioKey ? (
            <SavedAudioPlayback
              audioKey={audioKey}
              id={id || audioKey}
              activePlayerId={activePlayerId}
              onPlay={onPlay}
            />
          ) : null}
        </div>
      )
    }

    // No file yet — drop zone + click button
    return (
      <DropZone accept={ACCEPTED_AUDIO_TYPES} maxSizeMB={MAX_FILE_SIZE_MB} disabled={disabled} onFile={handleFile}>
        {({ isDragging }) => (
          <div
            className={`mt-2 flex flex-col items-center gap-2 rounded border border-dashed p-4 transition-colors ${
              isDragging ? `border-blue-400 bg-blue-50` : `border-default bg-surface-subtle`
            }`}
          >
            <p className="text-sm text-subtle">
              {isDragging ? `Drop your audio file here` : `Drag and drop your audio file here`}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_AUDIO_TYPES.join(`,`)}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (!file) return
                if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) return
                handleFile(file)
              }}
            />
            <Button variant="secondary" size="sm" disabled={disabled} onClick={() => fileInputRef.current?.click()}>
              Choose file to upload
            </Button>
          </div>
        )}
      </DropZone>
    )
  }

  return (
    <div className="relative mt-2">
      <Textarea
        className="bg-surface"
        rows={3}
        maxLength={MAX_GREETING_LENGTH}
        disabled={disabled}
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder={placeholder}
      />
      <span className="absolute bottom-2 right-3 text-body-xs text-minimal">
        {text.length}/{MAX_GREETING_LENGTH}
      </span>
    </div>
  )
}
